import React from 'react';
import { StyleSheet, View } from 'react-native';

import {
  BodyText,
  Button,
  ContentfulImageTransformOptions,
  DisplayText,
  FallbackImage,
  LabelText,
  theme,
  useResponsive,
} from '@sg/garnish';

import { assets } from '../../assets';
import { FlexLandingPageContent } from './FlexLandingPageContent';

export const FlexLandingPageHero = (props: FlexLandingPageHeroProps) => {
  const {
    title,
    disclaimerLabel,
    disclaimerDescription,
    heroImageXsUrl,
    heroImageXsA11y,
    heroImageSmUrl,
    heroImageSmA11y,
    eligibilityCta,
    isLoading,
    isEnrolled,
    onCtaPress,
  } = props;

  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <FlexLandingPageContent style={styles.containerXS}>
        <DisplayText sizeMatch={['40']} style={styles.fontColor}>
          {title}
        </DisplayText>

        <LabelText sizeMatch={['12']} style={[styles.label, styles.labelAlignmentXS]}>
          {disclaimerLabel}
        </LabelText>

        <BodyText sizeMatch={['16']} style={styles.fontColor}>
          {disclaimerDescription}
        </BodyText>

        <Button
          size="large"
          palette="cucumber"
          style={styles.ctaXS}
          disabled={isEnrolled}
          isLoading={isLoading}
          onPress={onCtaPress}
        >
          {eligibilityCta}
        </Button>

        <FallbackImage
          source={assets.wrapBanner}
          baseUrl={heroImageXsUrl}
          aria-label={heroImageXsA11y}
          style={styles.imageXS}
          contentfulOptions={CONTENTFUL_OPTIONS_XS}
        />
      </FlexLandingPageContent>
    );
  }

  return (
    <FlexLandingPageContent style={styles.containerSM}>
      <View style={styles.contentSM}>
        <View style={styles.contentInfoSM}>
          <DisplayText sizeMatch={['56']} style={styles.fontColor}>
            {title}
          </DisplayText>

          <Button
            size="large"
            palette="cucumber"
            style={styles.ctaSM}
            disabled={isEnrolled}
            isLoading={isLoading}
            onPress={onCtaPress}
          >
            {eligibilityCta}
          </Button>
        </View>

        <View style={styles.contentInfoSM}>
          <LabelText sizeMatch={['12']} style={[styles.label, styles.labelAlignmentSM]}>
            {disclaimerLabel}
          </LabelText>

          <BodyText sizeMatch={['16']} style={styles.fontColor}>
            {disclaimerDescription}
          </BodyText>
        </View>
      </View>

      <FallbackImage
        source={assets.harvestBowlBanner}
        baseUrl={heroImageSmUrl}
        aria-label={heroImageSmA11y}
        style={styles.imageSM}
        contentfulOptions={CONTENTFUL_OPTIONS_SM}
      />
    </FlexLandingPageContent>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const IMAGE_HEIGHT_XS = 240;
const CONTENTFUL_OPTIONS_XS: Partial<ContentfulImageTransformOptions> = {
  h: IMAGE_HEIGHT_XS,
  q: 70,
  fm: 'webp',
  fit: 'fill',
};

const IMAGE_HEIGHT_SM = 440;
const CONTENTFUL_OPTIONS_SM: Partial<ContentfulImageTransformOptions> = {
  h: IMAGE_HEIGHT_SM,
  q: 70,
  fm: 'webp',
  fit: 'fill',
};

const styles = StyleSheet.create({
  containerXS: {
    gap: theme.spacing['6'],
  },
  containerSM: {
    gap: theme.spacing['10'],
  },
  contentSM: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['10'],
  },
  contentInfoSM: {
    flex: 1,
    gap: theme.spacing['4'],
  },
  fontColor: {
    color: theme.colors.CHARCOAL,
  },
  label: {
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
    color: theme.colors.SPINACH,
    borderColor: theme.colors.CUCUMBER,
    borderWidth: 1,
    borderRadius: theme.radius['3xLarge'],
  },
  labelAlignmentXS: {
    alignSelf: 'flex-start',
  },
  labelAlignmentSM: {
    alignSelf: 'flex-end',
  },
  ctaXS: {
    height: 56,
  },
  ctaSM: {
    minWidth: 200,
    height: 60,
    alignSelf: 'flex-start',
  },
  imageXS: {
    width: '100%',
    height: IMAGE_HEIGHT_XS,
    borderRadius: theme.radius['xlarge'],
  },
  imageSM: {
    width: '100%',
    height: IMAGE_HEIGHT_SM,
    borderRadius: 40,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type FlexLandingPageHeroProps = {
  title: string;
  disclaimerLabel: string;
  disclaimerDescription: string;
  heroImageXsUrl?: string;
  heroImageXsA11y?: string;
  heroImageSmUrl?: string;
  heroImageSmA11y?: string;
  eligibilityCta: string;
  isLoading: boolean;
  isEnrolled: boolean;
  onCtaPress: () => void;
};
